import type { FC } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { SortableStopCard, SortableOrderGroup } from "@components";
import { EnhancedRouteItem } from "./types";
import { HiOutlineClipboardList, HiOutlineMap } from "react-icons/hi";
import {
  getPhysicalVisitKey,
  getRouteItemId,
} from "../../helpers/routeMappers";

interface DroppableZoneProps {
  id: string;
  stops: EnhancedRouteItem[];
  onMoveToRoute?: (stopId: string) => void;
  onMoveToAvailable?: (stopId: string) => void;
  onMoveOrderToRoute?: (orderId: string) => void;
  className?: string;
  invalidStopIds?: string[];
}

export const RouteDroppableZone: FC<DroppableZoneProps> = ({
  id,
  stops,
  onMoveToRoute,
  onMoveToAvailable,
  onMoveOrderToRoute,
  className,
  invalidStopIds = [],
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      id,
      accepts: id === "available-zone" ? ["route-zone"] : ["available-zone"],
      type: "container",
    },
  });
  const { t } = useTranslation();

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-32 text-muted text-sm">
      <HiOutlineClipboardList className="h-8 w-8 mb-2 opacity-50" />
      {id === "available-zone"
        ? t("planner:route_planner.no_available_stops")
        : t("planner:route_planner.no_scheduled_stops")}
    </div>
  );

  // Group stops by orderId for available zone
  const groupedStops = stops.reduce(
    (acc, stop) => {
      const orderId = stop.orderId || "ungrouped";
      if (!acc[orderId]) {
        acc[orderId] = [];
      }
      acc[orderId].push(stop);
      return acc;
    },
    {} as Record<string, EnhancedRouteItem[]>,
  );

  // Create an array of group IDs and stop IDs for SortableContext
  const groupIds = Object.keys(groupedStops).map(
    (orderId) => `group-${orderId}`,
  );
  const stopIds = stops.map(getRouteItemId);
  const items = [...groupIds, ...stopIds];
  const routeVisitGroups = Array.from(
    stops.reduce((groups, stop) => {
      const key = getPhysicalVisitKey(stop);
      groups.set(key, [...(groups.get(key) || []), stop]);
      return groups;
    }, new Map<string, EnhancedRouteItem[]>()),
  ).map(([, groupStops]) => groupStops);

  // For available zone, use grouped view
  if (id === "available-zone") {
    return (
      <div
        ref={setNodeRef}
        className={`w-full h-full ${isOver ? "bg-blue-50/30 dark:bg-blue-900/10" : ""} ${className || ""}`}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {Object.entries(groupedStops).map(([orderId, groupStops]) => {
              const displayName = groupStops[0]?.orderDisplayName || orderId;

              return (
                <SortableOrderGroup
                  key={`group-${orderId}`}
                  orderId={orderId}
                  groupStops={groupStops}
                  orderDisplayName={displayName}
                  onMoveToRoute={onMoveToRoute}
                  onMoveToAvailable={onMoveToAvailable}
                  onMoveOrderToRoute={onMoveOrderToRoute}
                  invalidStopIds={invalidStopIds}
                />
              );
            })}
            {Object.keys(groupedStops).length === 0 && <EmptyState />}
          </div>
        </SortableContext>
      </div>
    );
  }

  // For route zone, use sequence view with visual indicators
  return (
    <div
      ref={setNodeRef}
      className={`w-full min-w-0 max-w-full h-full flex flex-col overflow-hidden ${isOver ? "bg-blue-50/30 dark:bg-blue-900/10" : ""} ${className || ""}`}
    >
      {/* Route header with information */}
      {stops.length > 0 && (
        <div
          className={`flex items-center justify-between mb-3 ${isOver ? "text-blue-600 dark:text-blue-300" : ""}`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <HiOutlineMap
              className={`h-4 w-4 ${isOver ? "text-blue-400 dark:text-blue-300" : "text-muted"}`}
            />
            <span
              className={`text-sm font-medium ${isOver ? "text-blue-600 dark:text-blue-300" : "text-secondary"}`}
            >
              {t("planner:route_planner.route_sequence")}
            </span>
          </div>
          <span
            className={`ml-2 shrink-0 text-xs ${isOver ? "text-blue-500 dark:text-blue-300" : "text-muted"}`}
          >
            {routeVisitGroups.length}{" "}
            {routeVisitGroups.length === 1
              ? t("planner:route_planner.stop")
              : t("planner:route_planner.stops")}
          </span>
        </div>
      )}

      {/* Visual indicator for the route */}
      <div className="flex-1 flex flex-col min-h-0">
        <div
          className={`relative min-w-0 max-w-full flex-grow overflow-hidden
          ${isOver ? "border-blue-200 dark:border-blue-400" : "border-border-strong"}`}
        >
          <SortableContext
            items={stopIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="min-w-0 max-w-full space-y-2 pb-4">
              {routeVisitGroups.map((visitStops) => (
                <div
                  key={getPhysicalVisitKey(visitStops[0])}
                  className="w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-border bg-surface-muted/30 p-2 space-y-2"
                >
                  {visitStops.length > 1 && (
                    <div className="flex min-w-0 items-center justify-between gap-2 px-1 text-xs text-secondary">
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {visitStops[0].location.addressLine}
                      </span>
                      <span className="shrink-0">
                        {visitStops.length} {t("planner:route_planner.tasks")}
                      </span>
                    </div>
                  )}
                  {visitStops.map((stop) => (
                    <SortableStopCard
                      key={getRouteItemId(stop)}
                      stop={stop}
                      onMoveToRoute={
                        onMoveToRoute
                          ? () => onMoveToRoute(getRouteItemId(stop))
                          : undefined
                      }
                      onMoveToAvailable={
                        onMoveToAvailable
                          ? () => onMoveToAvailable(getRouteItemId(stop))
                          : undefined
                      }
                      isInvalid={invalidStopIds?.includes(stop.location.id)}
                      routeStops={stops}
                    />
                  ))}
                </div>
              ))}

              {/* Empty drop area that takes remaining space */}
              {isOver && stops.length > 0 && (
                <div className="h-12 border-2 border-dashed border-blue-300 dark:border-blue-500 rounded-lg bg-blue-50/50 dark:bg-blue-900/20 my-2"></div>
              )}
            </div>
          </SortableContext>

          {/* Empty state when no stops */}
          {stops.length === 0 && (
            <SortableContext
              items={stopIds}
              strategy={verticalListSortingStrategy}
            >
              <EmptyState />
            </SortableContext>
          )}
        </div>
      </div>
    </div>
  );
};
